'use client';

import { useEffect, useRef, useState } from 'react';
import type { Language } from '@/lib/api';
import { TAB_COUNT } from '@/lib/languagePrefs';
import { useFit } from '@/lib/useFit';
import LanguagePicker from './LanguagePicker';
import OverviewToggle from './OverviewToggle';
import NetworkChip from './NetworkChip';

/**
 * The language bar: a row of tabs above each pane, with the swap button on the
 * divider between them.
 *
 * Up to three languages are on show at a time and the whole set lives behind
 * the ▾ — which three is decided per browser (lib/languagePrefs) and then by
 * what the visitor actually uses. Picking from the panel promotes that language
 * onto the bar.
 *
 * HOW MANY of the three are drawn is whatever fits on one line (lib/useFit):
 * each half measures itself and lets go of the last language, then the one
 * before it, until the row fits — the selected one never, and the ▾ never,
 * since it is the way back to everything that went. A short name that still
 * fits after a long one had to go comes back. Three across a wide
 * window, two in a narrower one, one on a small phone; the bar is one line
 * tall at every size.
 */

interface Props {
  languages: Language[];
  from: string;
  to: string;
  sourceTabs: string[];
  targetTabs: string[];
  detected: { code: string; name: string } | null;
  lockedTarget: string | null;
  /**
   * The active mode answers in the language the user is writing in, so the whole
   * right-hand half of this bar is a choice that does not exist. Everything that
   * would offer one — the three target tabs, the ▾ panel and the swap button —
   * is hidden, and a plain line saying which language the answer will be in
   * takes their place.
   */
  followsInput: boolean;
  /**
   * The mode answers in the input language AND then shows that answer in the
   * output language too (Registers: formal, friendly and slang, each with a
   * translation card under it). The target tabs stay, because there is a
   * choice to make again — which language that second card is in — with a word
   * in front of them saying so. Swap stays hidden: the answer is still in the
   * language that was typed.
   */
  alsoTranslates?: boolean;
  /** Its name, for that line. Null while the detector is still deciding. */
  inputLangName: string | null;
  /**
   * Why swapping is unavailable right now, phrased for the tooltip — or null
   * when it is available. Decided by the Translator, which is the only thing
   * that knows whether there is a finished answer to move into the input box.
   */
  swapBlocked: string | null;
  /** `fromMenu` = chosen from the dropdown, which is the only case that may
   *  reorder the visible tabs. Clicking a tab must leave the bar alone. */
  onFrom: (code: string, fromMenu?: boolean) => void;
  onTo: (code: string, fromMenu?: boolean) => void;
  onSwap: () => void;
  /**
   * Drawn as a RECORD of a bar somebody used, not as a live one — the console's
   * translation dialog, replaying a stored row.
   *
   * It suppresses exactly one thing: the network chip. Everything else in this
   * bar is a rendering of the props it was handed, so a replay can simply hand
   * it the stored values; the chip is the one part that goes and MEASURES
   * something, and what it would measure in the console is the operator's own
   * connection at the moment they opened a row. Drawn inside a replica of
   * somebody else's screen, that number reads as a recorded fact about the
   * visitor, and it is not one — it is the only thing in this bar that can lie.
   */
  replay?: boolean;
  /** The Overview switch, which belongs to the target half of this bar. */
  overview: boolean;
  overviewAttention: boolean;
  onOverview: () => void;
  /** The translation level picker (components/LevelPicker), left of Overview. Absent in a replay. */
  levelPicker?: React.ReactNode;
}

/**
 * A language tab. The selected one carries a visible BORDER as well as a fill —
 * a tint alone is easy to miss against a white card, and this is the control
 * that tells you what the translator is about to do.
 */
function Tab({
  active,
  disabled,
  onClick,
  children,
  title,
  fit,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
  /** Its key in the row's drop order (lib/useFit); absent = it always stays. */
  fit?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={active}
      data-fit={fit}
      className={
        // `shrink-0` so a row that is too full OVERFLOWS, which is what the fit
        // measures; only a row with nothing left to drop lets a tab truncate.
        'shrink-0 truncate rounded-full border px-2.5 py-1.5 text-sm font-medium transition md:px-3 ' +
        '[[data-fit-tight]_&]:min-w-0 [[data-fit-tight]_&]:shrink ' +
        'disabled:cursor-not-allowed disabled:opacity-40 ' +
        (active
          // Dark mode keeps the tinted fill and border for identity but puts the
          // LABEL in plain white — coloured text on a coloured fill is the thing
          // that read as washed-out and unfinished.
          ? 'border-accent-500 bg-accent-200 text-accent-700 ' +
            'dark:border-accent-400 dark:bg-accent-600/25 dark:text-ink-dim'
          : // Transparent border on the inactive state so selecting one does not
            // shift the row by a pixel.
            'border-transparent text-ink-600 hover:bg-ink-100 ' +
            'dark:text-ink-300 dark:hover:bg-ink-800')
      }
    >
      {children}
    </button>
  );
}

function MoreButton({
  open,
  onClick,
  disabled,
}: {
  open: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-expanded={open}
      aria-haspopup="dialog"
      aria-label="All languages"
      title="All languages"
      className="flex shrink-0 items-center rounded-full px-2 py-1.5 text-ink-500 transition
                 hover:bg-ink-100 disabled:cursor-not-allowed disabled:opacity-40
                 dark:text-ink-400 dark:hover:bg-ink-800"
    >
      <svg viewBox="0 0 20 20" className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
           fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

export default function LanguageTabs({
  languages,
  from,
  to,
  sourceTabs,
  targetTabs,
  detected,
  lockedTarget,
  followsInput,
  alsoTranslates = false,
  inputLangName,
  swapBlocked,
  onFrom,
  onTo,
  onSwap,
  replay = false,
  overview,
  overviewAttention,
  onOverview,
  levelPicker,
}: Props) {
  const [openSide, setOpenSide] = useState<'source' | 'target' | null>(null);

  const name = (code: string) => languages.find((l) => l.code === code)?.name ?? code;
  // Swapping needs a concrete source: on "Detect language" that is whatever the
  // detector resolved to, and there is nothing to swap to until it has.
  // Nothing to swap when there is only one language in play — see `followsInput`.
  const canSwap = !swapBlocked && !lockedTarget && !followsInput;

  const visibleSource = sourceTabs.slice(0, TAB_COUNT);
  const visibleTarget = targetTabs.slice(0, TAB_COUNT);
  // What each half gives up when it runs out of room, first to go first: the
  // last language, then the one before it — never the selected one. On the
  // output side the connection readout goes before any language does: it is
  // something you read, and the languages are something you press.
  const lastFirst = (tabs: string[], selected: string) => tabs.filter((c) => c !== selected).reverse();
  const sourceFit = useFit<HTMLDivElement>(
    lastFirst(visibleSource, from),
    `${visibleSource.map(name).join('|')}|${from}|${detected?.name ?? ''}`,
    { refill: true },
  );
  const targetFit = useFit<HTMLDivElement>(
    ['net', ...lastFirst(visibleTarget, to)],
    `${visibleTarget.map(name).join('|')}|${to}|${followsInput}|${inputLangName ?? ''}`,
    { refill: true },
  );

  return (
    // z-20 lifts the bar (and its panels) above the panes below.
    <div className="relative z-20 rounded-2xl border border-ink-200 bg-white shadow-sm dark:border-ink-800 dark:bg-ink-900">
      {/* Same 1fr / 64px / 1fr template as the panes, so each half sits over the
          pane it controls and the swap button lines up with the mode column.
          These two templates must change together — if they drift, the tabs stop
          sitting above the pane they control. */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_64px_1fr]">
        {/* Source — above the input pane. The right border separates it from the
            swap button, so the bar reads as two distinct halves rather than one
            long row of pills. */}
        {/* `min-w-0`: a grid cell is otherwise as wide as its content at least,
            and a row that grows to hold its tabs is a row that never overflows. */}
        <div ref={sourceFit.ref}
             className="relative flex min-w-0 items-center gap-1 border-ink-200 px-2 py-2
                        md:border-r md:px-3 dark:border-ink-800">
          <Tab active={from === 'auto'} onClick={() => onFrom('auto')}>
            {detected && from === 'auto' ? `${detected.name} - Detected` : 'Detect language'}
          </Tab>
          {visibleSource.map((code) => (
            <Tab key={code} active={from === code} onClick={() => onFrom(code)} fit={code}>
              {name(code)}
            </Tab>
          ))}
          <MoreButton
            open={openSide === 'source'}
            onClick={() => setOpenSide(openSide === 'source' ? null : 'source')}
          />
          {openSide === 'source' && (
            <LanguagePicker
              languages={languages}
              selected={from}
              onPick={(c) => onFrom(c, true)}
              onClose={() => setOpenSide(null)}
              includeAuto
              onPickAuto={() => onFrom('auto')}
              detectedName={detected?.name ?? null}
            />
          )}
        </div>

        {/* Swap, in the middle cell — directly above the mode column. */}
        {/* The cell stays even when the button inside it does not: it is the
            64px column the mode buttons and the panes below line up with, and
            dropping it would shift the whole bar out from under them. */}
        <div className="flex items-center justify-center border-y border-ink-200 py-1
                        md:border-y-0 dark:border-ink-800">
          {!followsInput && (
            <SwapButton
              canSwap={canSwap}
              title={
                lockedTarget
                  ? 'This mode pins the answer language'
                  : swapBlocked ?? 'Swap languages and text'
              }
              onSwap={onSwap}
            />
          )}
        </div>

        {/* Target — above the output pane. */}
        <div ref={targetFit.ref}
             className="relative flex min-w-0 items-center gap-1 border-ink-200 px-2 py-2
                        md:border-l md:px-3 dark:border-ink-800">
          {followsInput && !alsoTranslates ? (
            /* Not a tab, deliberately: a pill here would look like something to
               press, and pressing it would have to do nothing. It is a
               statement about what is about to happen, in the place where the
               choice used to be. */
            <span
              className="flex shrink-0 items-center gap-1.5 px-2 py-1.5 text-sm text-ink-500
                         [[data-fit-tight]_&]:min-w-0 [[data-fit-tight]_&]:shrink dark:text-ink-400"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none"
                   stroke="currentColor" strokeWidth="1.7" aria-hidden>
                <path d="M4 7h7M7.5 7v10M14 17l3.5-9 3.5 9M15.2 14.3h5.6" strokeLinecap="round"
                      strokeLinejoin="round" />
              </svg>
              <span className="truncate">
                {inputLangName
                  ? `Answers in ${inputLangName}`
                  : 'Answers in the language you write in'}
              </span>
            </span>
          ) : (
            <>
              {followsInput && (
                <span className="shrink-0 pl-1 pr-0.5 text-xs font-medium text-ink-400"
                      title={`The answer is in ${inputLangName ?? 'the language you write in'}; a card under it gives the same text in the language chosen here`}>
                  Also in
                </span>
              )}
              {visibleTarget.map((code) => (
                <Tab
                  key={code}
                  active={to === code}
                  fit={code}
                  disabled={!!lockedTarget && lockedTarget !== code}
                  title={lockedTarget ? `This mode always answers in ${name(lockedTarget)}` : undefined}
                  onClick={() => onTo(code)}
                >
                  {name(code)}
                </Tab>
              ))}
              <MoreButton
                open={openSide === 'target'}
                onClick={() => setOpenSide(openSide === 'target' ? null : 'target')}
                disabled={!!lockedTarget}
              />
              {openSide === 'target' && (
                <LanguagePicker
                  languages={languages}
                  selected={to}
                  onPick={(c) => onTo(c, true)}
                  onClose={() => setOpenSide(null)}
                  align="right"
                />
              )}
            </>
          )}
          {/* Pushed to the far right of this half by the group's `ml-auto`, so
              they sit above the output pane's right edge.

              The connection readout goes to the LEFT of the switch, which keeps
              Overview exactly where anyone who has used this page before reaches
              for it — and puts the thing you only read where the eye passes on
              its way to the thing you press. */}
          <div className="ml-auto flex shrink-0 items-center gap-1.5">
            {/* The first thing this half lets go of when it is short of room
                (`net` in its drop order): it is a readout, not a control. */}
            {!replay && (
              <span data-fit="net" className="contents">
                <NetworkChip />
              </span>
            )}
            {levelPicker}
            <OverviewToggle on={overview} attention={overviewAttention} onToggle={onOverview} />
          </div>
        </div>
      </div>
    </div>
  );
}

function SwapButton({
  canSwap,
  title,
  onSwap,
}: {
  canSwap: boolean;
  /** Always says something: when the button is off, WHY it is off. */
  title: string;
  onSwap: () => void;
}) {
  /*
   * `aria-disabled`, not `disabled`, and that is the phone fix.
   *
   * A disabled button swallows the tap without a word, and the only thing that
   * said why was the `title` tooltip — which a touch screen never shows. On a
   * phone the button simply looked broken. Now a tap on a blocked swap puts the
   * reason in a small bubble under the button for a few seconds, on every
   * device, and the button still reads as off (dimmed, aria-disabled).
   */
  const [why, setWhy] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  useEffect(() => { if (canSwap) setWhy(null); }, [canSwap]);

  const press = () => {
    if (canSwap) { onSwap(); return; }
    setWhy(title);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setWhy(null), 2600);
  };

  return (
    <span className="relative inline-flex">
    <button
      type="button"
      onClick={press}
      aria-disabled={!canSwap}
      aria-label="Swap languages"
      title={title}
      className={
        'rounded-full border border-ink-200 bg-white p-2.5 text-ink-500 shadow-sm transition md:p-2 ' +
        'dark:border-ink-700 dark:bg-ink-800 dark:text-ink-300 ' +
        (canSwap ? 'hover:text-accent-600 active:scale-95' : 'cursor-not-allowed opacity-40')
      }
    >
      {/* Rotates on narrow screens, where the panes stack vertically and a
          left-right arrow would point the wrong way. */}
      <svg viewBox="0 0 24 24" className="h-4 w-4 rotate-90 md:rotate-0"
           fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M7 16H3m0 0l3-3m-3 3l3 3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17 8h4m0 0l-3-3m3 3l-3 3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M21 8H10M3 16h11" strokeLinecap="round" />
      </svg>
    </button>
    {why && (
      <span
        role="status"
        className="absolute left-1/2 top-full z-30 mt-1.5 w-max max-w-[16rem] -translate-x-1/2
                   rounded-lg bg-ink-900 px-2.5 py-1.5 text-center text-xs font-medium text-white
                   shadow-lg dark:bg-ink-100 dark:text-ink-900"
      >
        {why}
      </span>
    )}
    </span>
  );
}
